/**
 * TitlePoint Transaction Management
 * Centralized logic for managing TitlePoint API transactions, history, and status checking
 *
 * Mirrors the DataTrace transaction pattern for consistency
 */

import {
  getCredentials,
  buildGetRequestSummariesXML,
  buildGetResultByIdXML,
  callTitlePointAPI,
  parseSOAPResponse
} from './titlepointApi';

// Save transaction to persistent storage
export const saveTransaction = async (transaction) => {
  try {
    const response = await fetch('/api/titlepoint/save-transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction })
    });

    if (!response.ok) {
      throw new Error(`Failed to save transaction: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to save transaction:', error);
    throw error;
  }
};

// Load transaction history from server
export const loadTransactionHistory = async () => {
  try {
    const response = await fetch('/api/titlepoint/transaction-history');

    if (!response.ok) {
      throw new Error(`Failed to load transaction history: ${response.status}`);
    }

    const data = await response.json();
    return data.transactions || [];
  } catch (error) {
    console.error('Failed to load transaction history:', error);
    return [];
  }
};

// Update transaction status
export const updateTransactionStatus = async (requestId, statusData) => {
  try {
    const response = await fetch('/api/titlepoint/update-transaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestId, statusData })
    });

    if (!response.ok) {
      throw new Error(`Failed to update transaction: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to update transaction:', error);
    throw error;
  }
};

// Check transaction status via TitlePoint API (GetRequestSummaries)
export const checkTransactionStatus = async (requestId) => {
  const credentials = getCredentials();

  if (!credentials.username || !credentials.password) {
    throw new Error('TitlePoint credentials not configured');
  }

  try {
    const soapXML = buildGetRequestSummariesXML(requestId, credentials);

    const response = await callTitlePointAPI(
      credentials.serviceUrl,
      soapXML,
      credentials,
      'http://www.TitlePoint.com/GetRequestSummaries'
    );

    const parsed = parseSOAPResponse(response.responseXML || response.rawResponse);

    if (!parsed.success) {
      throw new Error(`SOAP Error: ${parsed.error}`);
    }

    // Extract status and result IDs from parsed response
    const statusInfo = extractStatusFromResponse(parsed.data);

    return {
      status: statusInfo.status,
      requestDetails: statusInfo,
      fullResponse: parsed.data,
      rawResponse: response.responseXML || response.rawResponse,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to check transaction status:', error);
    throw error;
  }
};

// Get transaction result (GetResultById)
export const getTransactionResult = async (resultId) => {
  const credentials = getCredentials();

  if (!credentials.username || !credentials.password) {
    throw new Error('TitlePoint credentials not configured');
  }

  try {
    const soapXML = buildGetResultByIdXML(resultId, credentials);

    const response = await callTitlePointAPI(
      credentials.serviceUrl,
      soapXML,
      credentials,
      'http://www.TitlePoint.com/GetResultById'
    );

    const parsed = parseSOAPResponse(response.responseXML || response.rawResponse);

    if (!parsed.success) {
      throw new Error(`SOAP Error: ${parsed.error}`);
    }

    return {
      resultData: parsed.data,
      fullResponse: parsed.data,
      rawResponse: response.responseXML || response.rawResponse,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to get transaction result:', error);
    throw error;
  }
};

// Helper: Extract status information from GetRequestSummaries response
function extractStatusFromResponse(data) {
  const statusInfo = {
    status: 'unknown',
    isComplete: false,
    resultIds: []
  };

  if (!data || typeof data !== 'object') {
    return statusInfo;
  }

  // Search for status indicators recursively
  const searchForStatus = (obj) => {
    if (!obj || typeof obj !== 'object') return;

    // Look for completion indicators
    if (obj.Status === 'Complete' || obj.status === 'Complete' ||
        obj.RequestStatus === 'Complete' || obj.requestStatus === 'Complete') {
      statusInfo.status = 'Complete';
      statusInfo.isComplete = true;
    } else if (obj.Status || obj.status) {
      statusInfo.status = obj.Status || obj.status;
    }

    // Look for ResultID fields
    if (obj.ResultID || obj.resultID) {
      const resultId = obj.ResultID || obj.resultID;
      if (!statusInfo.resultIds.includes(resultId)) {
        statusInfo.resultIds.push(resultId);
      }
    }

    // Recurse through nested objects and arrays
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        obj[key].forEach(item => searchForStatus(item));
      } else if (typeof obj[key] === 'object') {
        searchForStatus(obj[key]);
      }
    }
  };

  searchForStatus(data);

  return statusInfo;
}

// Check for duplicate searches
export const findDuplicateSearch = (transactionHistory, searchParams, serviceType) => {
  const searchKey = generateSearchKey(searchParams);

  return transactionHistory.find(t =>
    t.searchKey === searchKey &&
    t.serviceType === serviceType &&
    (t.status === 'pending' || t.status === 'processing')
  );
};

// Generate search key for deduplication
export const generateSearchKey = (searchParams) => {
  const parts = [];

  if (searchParams.address) parts.push(searchParams.address.trim());
  if (searchParams.city) parts.push(searchParams.city.trim());
  if (searchParams.state) parts.push(searchParams.state);
  if (searchParams.zip) parts.push(searchParams.zip.trim());

  return parts.join(', ').toLowerCase();
};

// Create transaction record for saving
export const createTransactionRecord = (searchParams, workflowType, apiResponse, requestBody) => {
  const searchKey = generateSearchKey(searchParams);

  // Extract requestId and orderId from response
  let requestId = null;
  let orderId = null;

  if (apiResponse.createResponse && apiResponse.createResponse.data) {
    const data = apiResponse.createResponse.data;
    requestId = findInObject(data, ['RequestID', 'requestId', 'requestID']);
    orderId = findInObject(data, ['OrderID', 'orderId', 'orderID']);
  }

  // Determine status from workflow result
  let status = 'pending';
  if (apiResponse.success === false || apiResponse.error) {
    status = 'failed';
  } else if (apiResponse.pollComplete) {
    status = 'complete';
  } else if (apiResponse.pollAttempts > 0) {
    status = 'processing';
  }

  // Build transaction record
  const transaction = {
    transactionId: requestId || `tp-${Date.now()}`,
    requestId: requestId,
    orderId: orderId,
    resultIds: apiResponse.resultIds || [],

    workflowType: workflowType, // 'property_search', 'title_search', 'resolve_property'
    serviceType: searchParams.serviceType || 'TitlePoint.Geo.Property',

    searchParams: {
      address: searchParams.address,
      city: searchParams.city,
      state: searchParams.state,
      zip: searchParams.zip,
      county: searchParams.county,
      apn: searchParams.apn
    },

    searchKey: searchKey,

    status: status,

    timestamp: apiResponse.timing?.started || new Date().toISOString(),
    lastChecked: new Date().toISOString(),
    completedAt: status === 'complete' ? new Date().toISOString() : null,

    requestPayload: requestBody,
    createResponse: apiResponse.createResponse,
    statusResponse: apiResponse.pollComplete ? {
      status: 'Complete',
      resultIds: apiResponse.resultIds
    } : null,
    resultResponses: apiResponse.results || [],

    error: apiResponse.error || null,

    timing: apiResponse.timing || null,
    pollAttempts: apiResponse.pollAttempts || 0
  };

  return transaction;
};

// Helper: Find a value in an object by trying multiple keys
function findInObject(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;

  // Try direct keys first
  for (const key of keys) {
    if (obj[key]) return obj[key];
  }

  // Search recursively
  for (const value of Object.values(obj)) {
    if (typeof value === 'object' && value !== null) {
      const found = findInObject(value, keys);
      if (found) return found;
    }
  }

  return null;
}

export default {
  saveTransaction,
  loadTransactionHistory,
  updateTransactionStatus,
  checkTransactionStatus,
  getTransactionResult,
  findDuplicateSearch,
  generateSearchKey,
  createTransactionRecord
};
